import * as React from 'react';
import {useEffect} from 'react';
import {Memo} from "@/common/dto/Memo";
import {LoadableView} from "@/component/view/LoadableView";
import {useAsyncEffect} from "@/hook/useAsyncEffect";
import {ApiClient} from "@/api/ApiClient";
import {FtButton} from "@/common/FtButton";
import {InputWrap} from "@/component/input/InputWrap";
import {MemoData} from "@/common/req/MemoData";
import {StringInput} from "@/component/input/StringInput";
import styled from "styled-components";
import {Device} from "@/store/Device";

const ListItem = styled.div`
    cursor: pointer;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    margin-bottom: 4px;
    font-size: 14px;

    &:hover {
        background-color: #f0f0f0;
    }

    &:active {
        background-color: #e0e0e0;
    }

    &.selected {
        background-color: #d0d0d0;
        font-weight: bold;
    }
`;

const MobileDetailContainer = styled.div`
    position: fixed;
    background: #fff;
    top: 70px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    padding: 16px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
    z-index: 1000;
    overflow-y: auto;
`;

export function MemoPage() {
    const [memos, setMemos] = React.useState<Memo[] | undefined>([]);
    const [selectedId, setSelectedId] = React.useState<number | undefined>(undefined);
    const [isAdding, setIsAdding] = React.useState(false);

    useAsyncEffect(async () => {
        setMemos(await ApiClient.instance.getMemos());
    }, []);

    function onCreated(memo: Memo) {
        setMemos(prev => prev ? [...prev, memo] : [memo]);
        setIsAdding(false);
    }

    function onUpdated(memo: Memo) {
        setMemos(prev => prev ? prev.map(m => m.id === memo.id ? memo : m) : [memo]);
    }

    const selectedMemo = memos?.find(memo => memo.id === selectedId);

    return <div>
        <h2>Memo</h2>

        <LoadableView data={memos} error={undefined} component={memos => {
            return <div style={{display: "flex", gap: 16}}>
                <div style={{width: Device.isMobile ? "100%" : 300}}>
                    {memos.map(memo => (
                        <ListItem key={memo.id}
                                  onClick={() => setSelectedId(memo.id)}
                                  className={memo.id === selectedId ? "selected" : ""}
                        >{memo.title}</ListItem>
                    ))}

                    <FtButton fullWidth variant="outlined" onClick={() => {
                        setSelectedId(undefined);
                        setIsAdding(true);
                    }}>+</FtButton>
                </div>

                {selectedMemo && <DetailWrap><MemoView memo={selectedMemo} handleUpdated={onUpdated}
                                                       handleClose={() => setSelectedId(undefined)}/></DetailWrap>}

                {isAdding && <DetailWrap><AddMemoView
                    handleCancel={() => setIsAdding(false)}
                    handleCreated={onCreated}
                /></DetailWrap>}
            </div>
        }}/>
    </div>
}

function DetailWrap({children}: { children: React.ReactNode }) {
    if (Device.isMobile) {
        return <MobileDetailContainer>{children}</MobileDetailContainer>;
    } else {
        return <div style={{flexGrow: 1}}>{children}</div>;
    }
}

function MemoView({memo, handleUpdated, handleClose}: {
    memo: Memo,
    handleUpdated: (memo: Memo) => void,
    handleClose: () => void
}) {
    const [isEditing, setIsEditing] = React.useState(false);

    useEffect(() => {
        setIsEditing(false);
    }, [memo]);

    async function updateAction(data: MemoData) {
        if (!data.title) return;
        const updated = await ApiClient.instance.updateMemo(memo.id, data);
        setIsEditing(false);
        handleUpdated(updated);
    }

    return <div>
        {!isEditing && <div>
            <h3 style={{margin: 0, padding: 0}}>{memo.title}</h3>
            <div style={{marginTop: 16, whiteSpace: "pre-wrap", fontSize: 14}}>{memo.content}</div>

            <div style={{display: "flex", gap: 8, marginTop: 16}}>
                <FtButton onClick={() => setIsEditing(true)}>수정</FtButton>
                {Device.isMobile && <FtButton variant="outlined" onClick={handleClose}>닫기</FtButton>}
            </div>
        </div>}
        {isEditing && <MemoForm initialData={memo}
                                doneLabel="수정"
                                handleCancel={() => setIsEditing(false)}
                                handleDone={updateAction}
        />}
    </div>;
}

function AddMemoView(props: { handleCreated: (memo: Memo) => void, handleCancel: () => void }) {
    async function createAction(data: MemoData) {
        if (!data.title) return;
        const newMemo = await ApiClient.instance.createMemo(data);
        props.handleCreated(newMemo);
    }

    return <MemoForm initialData={{title: "", content: ""}}
                     doneLabel="생성"
                     handleCancel={props.handleCancel}
                     handleDone={createAction}/>;
}

function MemoForm(props: {
    initialData: MemoData;
    doneLabel: string;
    handleCancel: () => void;
    handleDone: (data: MemoData) => void;
}) {
    const [title, setTitle] = React.useState(props.initialData.title);
    const [content, setContent] = React.useState(props.initialData.content);

    const isValid = title.trim().length > 0;

    return <div>
        <InputWrap label="Title">
            <StringInput value={title} onChange={setTitle} fullWidth={true}/>
        </InputWrap>

        <InputWrap label="Conttent">
            <textarea style={{width: "100%", height: 200}} value={content} onChange={e => setContent(e.target.value)}/>
        </InputWrap>

        <div style={{display: "flex", gap: 8}}>
            <FtButton variant="outlined" onClick={props.handleCancel}>취소</FtButton>
            <div style={{width: 100}}>
                <FtButton variant="contained"
                          disabled={!isValid}
                          fullWidth={true}
                          onClick={() => props.handleDone({title, content})}>{props.doneLabel}</FtButton>
            </div>
        </div>
    </div>;
}